import { buildErrorsCenterEntries } from "./errorsCenterData.js";
import { getFleetProductTone } from "./cropColors.js";
import { getAssignmentDisplayedWorkloadMinutes, getAssignmentMaxWorkMinutes } from "./resultsFleetData.js";
import {
  buildDispatchSourceStaleNotice,
  collectFleetOptimizationExcludedTrucks,
  collectVisibleOrderProtectionSummary,
  resolveLiveSuspendedDeliveryStats,
  resolveServiceValidationMeta,
} from "./resultsChromeData.js";

export const DEFAULT_RIGHT_PANEL_COST_LABEL = "Dépenses";

const RESULTS_RIGHT_PANEL_STATE = {
  panelData: null,
  serviceValidation: null,
};

const resultsRightPanelListeners = new Set();

function emitListeners(listeners) {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // Ignore listener errors to keep right-panel updates stable.
    }
  });
}

function subscribeListener(listeners, listener) {
  if (typeof listener !== "function") {
    return () => {};
  }

  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function isSameFlatObject(left, right) {
  if (left === right) {
    return true;
  }
  if (!left || !right) {
    return false;
  }

  const leftKeys = Object.keys(left);
  return leftKeys.length === Object.keys(right).length && leftKeys.every((key) => left[key] === right[key]);
}

export function getResultsRightPanelSnapshot() {
  return RESULTS_RIGHT_PANEL_STATE.panelData;
}

// Sélecteurs stables : l'en-tête et les onglets ne se redessinent que si leur valeur change.
export function getResultsServiceValidationSnapshot() {
  return RESULTS_RIGHT_PANEL_STATE.serviceValidation;
}

export function getResultsRightPanelAlertCountSnapshot() {
  return RESULTS_RIGHT_PANEL_STATE.panelData?.alertCount ?? null;
}

export function getResultsRightPanelWarningCountSnapshot() {
  return RESULTS_RIGHT_PANEL_STATE.panelData?.warningCount ?? null;
}

export function subscribeResultsRightPanel(listener) {
  return subscribeListener(resultsRightPanelListeners, listener);
}

export function publishResultsRightPanelData(panelData = null) {
  RESULTS_RIGHT_PANEL_STATE.panelData = panelData && typeof panelData === "object"
    ? panelData
    : null;
  const nextServiceValidation = RESULTS_RIGHT_PANEL_STATE.panelData?.serviceValidation || null;
  if (!isSameFlatObject(RESULTS_RIGHT_PANEL_STATE.serviceValidation, nextServiceValidation)) {
    RESULTS_RIGHT_PANEL_STATE.serviceValidation = nextServiceValidation;
  }
  emitListeners(resultsRightPanelListeners);
}

// Durées affichées par les lignes du Gantt, pour que la marge montre exactement les mêmes.
const FLEET_ROW_DURATION_STATE = {
  byKey: new Map(),
  isEmitScheduled: false,
  snapshot: new Map(),
};

const fleetRowDurationListeners = new Set();

export function buildFleetRowDurationKey(dispatchDate = "", truckId = "") {
  return `${dispatchDate || ""}|${truckId || ""}`;
}

export function reportFleetRowDisplayedMinutes(key = "", minutes = null) {
  if (!key) {
    return;
  }

  const value = Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : null;
  if ((FLEET_ROW_DURATION_STATE.byKey.get(key) ?? null) === value) {
    return;
  }

  if (value === null) {
    FLEET_ROW_DURATION_STATE.byKey.delete(key);
  } else {
    FLEET_ROW_DURATION_STATE.byKey.set(key, value);
  }

  if (FLEET_ROW_DURATION_STATE.isEmitScheduled) {
    return;
  }

  FLEET_ROW_DURATION_STATE.isEmitScheduled = true;
  queueMicrotask(() => {
    FLEET_ROW_DURATION_STATE.isEmitScheduled = false;
    FLEET_ROW_DURATION_STATE.snapshot = new Map(FLEET_ROW_DURATION_STATE.byKey);
    emitListeners(fleetRowDurationListeners);
  });
}

export function getFleetRowDisplayedMinutesSnapshot() {
  return FLEET_ROW_DURATION_STATE.snapshot;
}

export function subscribeFleetRowDisplayedMinutes(listener) {
  return subscribeListener(fleetRowDurationListeners, listener);
}

function toNumber(value) {
  const resolvedValue = Number(value);
  return Number.isFinite(resolvedValue) ? resolvedValue : null;
}

function roundCurrency(value) {
  if (!Number.isFinite(value)) {
    return null;
  }

  return Math.round(value * 100) / 100;
}

function formatCurrencyLabel(value) {
  if (!Number.isFinite(value)) {
    return "";
  }

  return new Intl.NumberFormat("fr-CA", {
    style: "currency",
    currency: "CAD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDistanceLabel(value) {
  if (!Number.isFinite(value)) {
    return "";
  }

  return `${Math.round(value * 10) / 10} km`;
}

function formatDurationLabel(value) {
  if (!Number.isFinite(value) || value < 0) {
    return "";
  }

  const roundedMinutes = Math.round(value);
  const hours = Math.floor(roundedMinutes / 60);
  const remainingMinutes = roundedMinutes % 60;

  if (hours <= 0) {
    return `${remainingMinutes} min`;
  }

  if (remainingMinutes === 0) {
    return `${hours} h`;
  }

  return `${hours} h ${remainingMinutes} min`;
}

function getAssignmentTripCount(assignment) {
  const tripCount = toNumber(assignment?.trip_count);
  if (tripCount !== null) {
    return tripCount;
  }

  return Array.isArray(assignment?.trips) ? assignment.trips.length : 0;
}

function sumAssignmentCost(assignments) {
  return assignments.reduce((totalCost, assignment) => {
    const assignmentCost = toNumber(assignment?.total_estimated_cost);
    return totalCost + (assignmentCost || 0);
  }, 0);
}

function buildEstimatedCostItemMeta(assignment) {
  const tripCount = getAssignmentTripCount(assignment);
  const parts = [];

  if (tripCount > 0) {
    parts.push(tripCount <= 1 ? "1 tournée" : `${tripCount} tournées`);
  }

  const distanceLabel = typeof assignment?.total_estimated_distance_label === "string" && assignment.total_estimated_distance_label.trim()
    ? assignment.total_estimated_distance_label.trim()
    : formatDistanceLabel(toNumber(assignment?.total_estimated_distance_km));
  if (distanceLabel) {
    parts.push(distanceLabel);
  }

  const durationLabel = typeof assignment?.total_estimated_duration_label === "string" && assignment.total_estimated_duration_label.trim()
    ? assignment.total_estimated_duration_label.trim()
    : formatDurationLabel(toNumber(assignment?.total_estimated_duration_minutes));
  if (durationLabel) {
    parts.push(durationLabel);
  }

  return parts.join(" · ");
}

function buildEstimatedCostHoverCard(assignments, totalEstimatedCost, costLabel = DEFAULT_RIGHT_PANEL_COST_LABEL) {
  const costedAssignments = Array.isArray(assignments)
    ? assignments.filter((assignment) => toNumber(assignment?.total_estimated_cost) !== null)
    : [];

  if (!costedAssignments.length || !Number.isFinite(totalEstimatedCost)) {
    return null;
  }

  const totalDistance = costedAssignments.reduce((sum, assignment) => sum + (toNumber(assignment?.total_estimated_distance_km) || 0), 0);
  const totalDuration = costedAssignments.reduce((sum, assignment) => sum + (toNumber(assignment?.total_estimated_duration_minutes) || 0), 0);

  return {
    type: "estimated-cost-breakdown",
    items: costedAssignments.map((assignment, index) => ({
      key: assignment?.truck_id || assignment?.truck_name || `estimated-cost-${index}`,
      label: assignment?.truck_name || assignment?.truck_id || "Camion",
      meta: buildEstimatedCostItemMeta(assignment),
      value: typeof assignment?.total_estimated_cost_label === "string" && assignment.total_estimated_cost_label.trim()
        ? assignment.total_estimated_cost_label.trim()
        : formatCurrencyLabel(toNumber(assignment?.total_estimated_cost)),
    })),
    lines: [
      {
        label: "Camions chiffrés",
        value: String(costedAssignments.length),
      },
      totalDistance > 0
        ? {
            label: "Distance",
            value: formatDistanceLabel(totalDistance),
          }
        : null,
      totalDuration > 0
        ? {
            label: "Durée",
            value: formatDurationLabel(totalDuration),
          }
        : null,
    ].filter(Boolean),
    note: "Le total additionne les coûts estimés de chaque camion affecté. Chaque montant camion reprend le coût estimé de sa tournée calculé à partir du routage et des paramètres du camion.",
    total: formatCurrencyLabel(totalEstimatedCost),
    totalLabel: costLabel,
  };
}

function formatClockLabel(isoDateTime = "") {
  const clock = typeof isoDateTime === "string" ? isoDateTime.slice(11, 16) : "";
  return /^\d{2}:\d{2}$/.test(clock) ? clock.replace(":", " h ") : "";
}

function capitalizeLabel(value = "") {
  const text = String(value || "").trim();
  return text ? `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}` : "";
}

function toLocalIsoDate(date = new Date()) {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function shiftIsoDate(isoDate, dayOffset) {
  const [year, month, day] = isoDate.split("-").map(Number);
  return toLocalIsoDate(new Date(year, month - 1, day + dayOffset));
}

export function formatRightPanelDayLabel(isoDate = "") {
  const [year, month, day] = String(isoDate).split("-").map(Number);
  if (!year || !month || !day) {
    return isoDate;
  }

  return new Intl.DateTimeFormat("fr-CA", { day: "numeric", month: "short" }).format(new Date(year, month - 1, day));
}

// Charge, kilomètres à vide, produits livrés et chargements par meunerie en un seul passage sur les arrêts.
function summarizeRouteStops(usedAssignments) {
  const mills = new Map();
  const products = new Map();
  let emptyDistanceKm = 0;

  for (const assignment of usedAssignments) {
    for (const trip of assignment?.trips || []) {
      const segmentDistances = Array.isArray(trip?.route?.segment_distances) ? trip.route.segment_distances : [];
      let loadTons = 0;

      (trip?.stops || []).forEach((stop, index) => {
        const quantityTons = toNumber(stop?.quantity_tons) || 0;
        if (stop?.stop_type === "loading") {
          const millName = stop?.label || stop?.client_name || "Chargement";
          const arrival = typeof stop?.display_arrival_at === "string" ? stop.display_arrival_at.slice(11, 16) : "";
          const mill = mills.get(millName) || { firstArrival: "", lastArrival: "", name: millName, tons: 0, truckIds: new Set() };
          mill.tons += quantityTons;
          mill.truckIds.add(assignment?.truck_id);
          if (arrival && (!mill.firstArrival || arrival < mill.firstArrival)) {
            mill.firstArrival = arrival;
          }
          if (arrival && arrival > mill.lastArrival) {
            mill.lastArrival = arrival;
          }
          mills.set(millName, mill);
          loadTons += quantityTons;
        } else if (stop?.stop_type === "delivery") {
          const productKey = String(stop?.grain_type || "").trim().toLowerCase();
          if (productKey) {
            const product = products.get(productKey) || { label: capitalizeLabel(productKey), tons: 0 };
            product.tons += quantityTons;
            products.set(productKey, product);
          }
          loadTons -= quantityTons;
        }

        if (index < segmentDistances.length && loadTons <= 0.001) {
          emptyDistanceKm += toNumber(segmentDistances[index]?.distance_km) || 0;
        }
      });
    }
  }

  return {
    emptyDistanceKm,
    mills: Array.from(mills.values())
      .map((mill) => ({
        firstArrival: mill.firstArrival,
        lastArrival: mill.lastArrival,
        name: mill.name,
        tons: Math.round(mill.tons * 10) / 10,
        truckCount: mill.truckIds.size,
      }))
      .sort((left, right) => right.tons - left.tons),
    products: Array.from(products.values())
      .map((product) => ({
        accentColor: getFleetProductTone(product.label)?.accentColor || "#94a3b8",
        label: product.label,
        tons: Math.round(product.tons * 10) / 10,
      }))
      .sort((left, right) => right.tons - left.tons),
  };
}

function resolveDispatchDayStatus(day) {
  if (day?.is_closed) {
    return { label: "Fermée", tone: "neutral" };
  }
  if (day?.dispatch_status === "livre") {
    return { label: "Livrée", tone: "neutral" };
  }
  if (day?.requires_revalidation || day?.dispatch_status === "a_revalider") {
    return { label: "À revalider", tone: "amber" };
  }
  if (!day?.last_optimized_at) {
    return { label: "Non optimisé", tone: "neutral" };
  }
  return { label: "Optimisé", tone: "neutral" };
}

function buildOtherDaysModel(dispatchDays = [], today = toLocalIsoDate()) {
  const days = Array.isArray(dispatchDays)
    ? dispatchDays.filter((day) => typeof day?.dispatch_date === "string" && (toNumber(day?.order_count) || 0) > 0)
    : [];
  const byDate = (left, right) => left.dispatch_date.localeCompare(right.dispatch_date);
  const todayDay = days.find((day) => day.dispatch_date === today) || null;
  const upcoming = days
    .filter((day) => day.dispatch_date > today && !day.is_closed && (!day.last_optimized_at || day.requires_revalidation))
    .sort(byDate)
    .slice(0, 2);
  const toClose = days.filter((day) => day.dispatch_date < today && !day.is_closed).sort(byDate);
  const recentLimit = shiftIsoDate(today, -14);
  const recentToClose = toClose.filter((day) => day.dispatch_date >= recentLimit);

  return {
    rows: [
      todayDay
        ? {
            dispatchDate: todayDay.dispatch_date,
            label: `${formatRightPanelDayLabel(todayDay.dispatch_date)} · aujourd'hui`,
            note: `${todayDay.order_count} commandes`,
            status: resolveDispatchDayStatus(todayDay),
          }
        : null,
      ...upcoming.map((day) => ({
        dispatchDate: day.dispatch_date,
        label: formatRightPanelDayLabel(day.dispatch_date),
        note: `${day.order_count} commandes`,
        status: resolveDispatchDayStatus(day),
      })),
      toClose.length
        ? {
            dispatchDate: toClose[toClose.length - 1].dispatch_date,
            label: toClose.length > 1 ? `${toClose.length} journées passées` : "1 journée passée",
            note: [
              recentToClose.map((day) => formatRightPanelDayLabel(day.dispatch_date)).join(", "),
              toClose.length > recentToClose.length ? `${toClose.length - recentToClose.length} plus anciennes` : "",
            ].filter(Boolean).join(" + "),
            status: { label: "À clore", tone: "amber" },
          }
        : null,
    ].filter(Boolean),
  };
}

function buildDispatchDayModel({ planningSummary = {}, resultsData = {}, assignments = [], today = toLocalIsoDate() }) {
  const usedAssignments = assignments.filter((assignment) => getAssignmentTripCount(assignment) > 0);
  const routeSummary = summarizeRouteStops(usedAssignments);
  const { suspendedCount, suspendedQuantityTons } = resolveLiveSuspendedDeliveryStats(resultsData, planningSummary);
  const protectionSummary = collectVisibleOrderProtectionSummary(assignments, resultsData);
  const excludedTrucks = collectFleetOptimizationExcludedTrucks(assignments, resultsData);
  const planningUpdatedAt = typeof planningSummary?.updated_at === "string" ? planningSummary.updated_at.trim() : "";
  const staleNotice = planningSummary?.is_dispatch_day_closed === true
    ? null
    : buildDispatchSourceStaleNotice(resultsData, { planningUpdatedAt });
  const totalEstimatedCost = roundCurrency(sumAssignmentCost(usedAssignments));
  const displayStarts = usedAssignments.map((assignment) => assignment?.display_start_at).filter(Boolean).sort();
  const displayEnds = usedAssignments.map((assignment) => assignment?.display_end_at).filter(Boolean).sort();
  const canOptimize = planningSummary?.can_optimize !== false && planningSummary?.is_dispatch_day_closed !== true;

  return {
    canOptimize,
    costDetails: buildEstimatedCostHoverCard(usedAssignments, totalEstimatedCost),
    days: buildOtherDaysModel(resultsData?.dispatch_days, today),
    dispatchDate: typeof resultsData?.dispatch_date === "string" ? resultsData.dispatch_date : "",
    distanceKm: usedAssignments.reduce((sum, assignment) => sum + (toNumber(assignment?.total_estimated_distance_km) || 0), 0),
    emptyDistanceKm: routeSummary.emptyDistanceKm,
    excludedTrucks: excludedTrucks.map((truck) => ({ label: truck.truckLabel, reason: truck.reasonLabel })),
    firstStartLabel: formatClockLabel(displayStarts[0]),
    lastEndLabel: formatClockLabel(displayEnds[displayEnds.length - 1]),
    mills: routeSummary.mills,
    orderCount: toNumber(resultsData?.assigned_orders) || 0,
    plannedTons: toNumber(resultsData?.assigned_quantity_tons)
      ?? usedAssignments.reduce((sum, assignment) => sum + (toNumber(assignment?.total_quantity_tons) || 0), 0),
    products: routeSummary.products,
    protectedOrderTotal: protectionSummary.totalVisibleOrders || 0,
    staleNotice: staleNotice
      ? { causeLines: Array.isArray(staleNotice.causeLines) ? staleNotice.causeLines : [], updatedAtLabel: staleNotice.updatedAtLabel || "" }
      : null,
    suspendedCount,
    suspendedTons: suspendedQuantityTons,
    timeLimitThresholdPct: toNumber(resultsData?.fleet_time_limit_threshold_pct) || 90,
    totalEstimatedCost,
    trucks: assignments.map((assignment) => ({
      backendMinutes: getAssignmentDisplayedWorkloadMinutes(assignment),
      capacityTons: toNumber(assignment?.capacity_tons) || 0,
      compartmentsUsed: toNumber(assignment?.compartments_used) || 0,
      compartmentsTotal: toNumber(assignment?.total_compartments) || 0,
      distanceKm: toNumber(assignment?.total_estimated_distance_km) || 0,
      endOfDayTargetDate: (assignment?.end_of_day_loads || []).map((load) => load?.target_dispatch_date).find(Boolean) || "",
      hasEndOfDayLoad: (toNumber(assignment?.end_of_day_load_count) || 0) > 0,
      id: assignment?.truck_id || "",
      isDispatchDisabled: assignment?.is_dispatch_disabled === true,
      isUsed: getAssignmentTripCount(assignment) > 0,
      maxMinutes: getAssignmentMaxWorkMinutes(assignment),
      name: assignment?.truck_name || assignment?.truck_id || "Camion",
      peakTons: toNumber(assignment?.peak_trip_load_tons) || 0,
    })),
    unprotectedOrderCount: protectionSummary.totalVisibleOrders > 0
      ? protectionSummary.unprotectedOrderIds.length
      : (toNumber(planningSummary?.pending_count) || 0),
  };
}

// Combine le modèle de la journée avec les durées affichées par le Gantt.
export function resolveRightPanelTruckRows(day = null, displayedMinutesByKey = new Map()) {
  const trucks = Array.isArray(day?.trucks) ? day.trucks : [];
  const threshold = day?.timeLimitThresholdPct || 90;
  const usedRows = trucks
    .filter((truck) => truck.isUsed)
    .map((truck) => {
      const minutes = displayedMinutesByKey.get(buildFleetRowDurationKey(day.dispatchDate, truck.id)) ?? truck.backendMinutes;
      const pct = truck.maxMinutes > 0 ? (minutes / truck.maxMinutes) * 100 : 0;
      return { ...truck, minutes, pct, isNearLimit: pct >= threshold };
    })
    .sort((left, right) => right.pct - left.pct);

  return {
    disabledTrucks: trucks.filter((truck) => truck.isDispatchDisabled),
    freeTrucks: trucks.filter((truck) => !truck.isUsed && !truck.isDispatchDisabled),
    totalMinutes: usedRows.reduce((sum, row) => sum + row.minutes, 0),
    usedRows,
  };
}

export function buildResultsRightPanelData(source = {}, fallbackData = null, { today } = {}) {
  const resultsData = source?.resultsData && typeof source.resultsData === "object"
    ? source.resultsData
    : (source && typeof source === "object" ? source : {});
  const planningSummary = source?.planningSummary && typeof source.planningSummary === "object"
    ? source.planningSummary
    : (resultsData?.planning_summary && typeof resultsData.planning_summary === "object" ? resultsData.planning_summary : {});
  const assignments = Array.isArray(source?.assignmentList)
    ? source.assignmentList.filter(Boolean)
    : (Array.isArray(resultsData?.assignments) ? resultsData.assignments.filter(Boolean) : []);
  const serviceValidation = resolveServiceValidationMeta(resultsData);

  return {
    alertCount: buildErrorsCenterEntries(resultsData, { viewName: "errors" }).length,
    day: buildDispatchDayModel({ assignments, planningSummary, resultsData, today }),
    serviceValidation: {
      errorFilter: serviceValidation.errorFilter,
      errorSource: serviceValidation.errorSource,
      errorViewName: serviceValidation.errorViewName,
      isInteractive: serviceValidation.isInteractive,
      label: serviceValidation.label,
      note: serviceValidation.note,
      shouldPulse: serviceValidation.shouldPulse,
      tone: serviceValidation.tone,
      value: serviceValidation.value,
    },
    title: fallbackData?.title || "Panneau droit",
    warningCount: buildErrorsCenterEntries(resultsData, { viewName: "warnings" }).length,
  };
}
